import { compose, lifecycle, withState, withProps } from "recompose";
import React from "react";
import styled from "styled-components";
import Slider from "react-slick";
import { ModalTitle, ModalSubtitle } from "../common/Modal";
import { Cta } from "../common/Button";
import Loading from "./Loading";

const MODAL_MAX_HEIGHT = 650;
const MODAL_MAX_WIDTH = 760;

const getTemplateHeight = () => {
  const heightUsed = 210;
  let templateHeight = window.innerHeight - heightUsed;
  if (window.innerWidth >= 640 && templateHeight > MODAL_MAX_HEIGHT - heightUsed) {
    templateHeight = MODAL_MAX_HEIGHT - heightUsed;
  }
  const aspectRatio = 16 / 9;
  const templateWidth = templateHeight / aspectRatio;
  const widthMargin = 20;
  const carouselWidth = window.innerWidth > 640 ? 640 : window.innerWidth - widthMargin;
  return { templateHeight, templateWidth, carouselWidth };
};

const {
  templateHeight: TEMPLATE_VIDEO_HEIGHT,
  templateWidth: TEMPLATE_VIDEO_WIDTH,
  carouselWidth: CAROUSEL_WIDTH,
} = getTemplateHeight();

const ChooseVideoTemplate = ({
  videoTemplates,
  onChooseTemplate,
  onVideoTemplateSlideTo,
  videoTemplatesLoading,
  videoTemplatesError,
  currentVideoTemplate,
  activeTemplateIndex,
}) => {
  let content = <Loading />;

  if (videoTemplatesError || !videoTemplates) {
    content = (
      <ModalSubtitle>
        {videoTemplatesError || "Sorry, an unexpected error has ocurred, please try again."}
      </ModalSubtitle>
    );
  } else if (!videoTemplatesLoading && currentVideoTemplate) {
    content = (
      <VideoTemplatesSlider
        videoTemplates={videoTemplates}
        onVideoTemplateSlideTo={onVideoTemplateSlideTo}
        activeTemplateIndex={activeTemplateIndex}
      />
    );
  }

  return (
    <ChooseVideoTemplatesLayout>
      <ModalTitle style={{ marginBottom: 10 }}>Choose a template</ModalTitle>
      <LayoutBlock large>{content}</LayoutBlock>
      <ButtonContainer>
        <Cta disabled={!currentVideoTemplate} onClick={onChooseTemplate} style={{ marginTop: 0 }}>
          Create Video Story
        </Cta>
      </ButtonContainer>
    </ChooseVideoTemplatesLayout>
  );
};

const ButtonContainer = styled.div`
  width: 80%;
  margin-top: 10px;
`;

const ChooseVideoTemplatesLayout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
`;

const LayoutBlock = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding-bottom: 20px;
`;

const videoSliderSettings = {
  dots: true,
  speed: 500,
  slidesPerRow: 1,
  slidesToShow: 1,
  slidesToScroll: 1,
  initialSlide: 0,
  centerMode: true,
};

const VideoTemplatesSlider = ({ videoTemplates, onVideoTemplateSlideTo, activeTemplateIndex }) => {
  if (!videoTemplates) return null;
  return (
    <SliderContainer>
      <Slider
        {...videoSliderSettings}
        afterChange={onVideoTemplateSlideTo}
        style={{ paddingBottom: "10px", height: "100%" }}
      >
        {videoTemplates.map((vt, i) => (
          <VideoTemplateSlide videoTemplate={vt} key={i} />
        ))}
      </Slider>
    </SliderContainer>
  );
};

const SliderContainer = styled.div`
  max-width: ${CAROUSEL_WIDTH}px;
  max-height: ${TEMPLATE_VIDEO_HEIGHT}px;
  @media (min-width: 640px) {
    width: unset;
    max-width: 386px;
  }
`;

const VideoTemplateSlide = ({ videoTemplate }) => (
  <VideoContainer>
    <video loop muted autoPlay playsInline preload="auto" controlsList="nodownload nofullscreen noremoteplayback">
      <source src={videoTemplate.coverVideo.url} type="video/mp4" />
      Your browser doesn't support video playback
    </video>
  </VideoContainer>
);

const VideoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0px auto;
  padding: 0px 10px;
  width: 100%;
  height: ${TEMPLATE_VIDEO_HEIGHT}px;
  & > video {
    object-fit: contain;
    margin: 0 auto;
    height: 98%;
    background-image: url("/assets/images/9load.gif");
    background-size: 48px 48px;
    background-position: center center;
    background-repeat: no-repeat;
    border: none;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 10px 5px -6px, rgba(0, 0, 0, 0.2) 0px 0px 5px -3px;
  }
`;

const enhancer = compose(
  lifecycle({
    componentWillReceiveProps(nextProps) {
      if (nextProps.open && !this.props.open) {
        if (window.gtag) {
          window.gtag("event", "page_view", { title: "Choose Template" });
        }
      }
    },
  })
);

export default enhancer(ChooseVideoTemplate);
